import { Box, Tooltip, Typography } from "@mui/material";
import { ReactNode } from "react";
import { useCurrency } from "../../../context/CurrencyContext";
import { useTheme } from "../../../context/ThemeContext";
import { Sx, TABULAR_NUMERALS, Tone, toneColors } from "./shared";

/* =======================================================================
   Metric — label, figure, one line of context
   ======================================================================= */

export interface MetricProps {
  label: string;
  /**
   * Numbers are rounded for display and shown in full on hover. Pass a string
   * for values that are not a single quantity (e.g. "12 / 40"), or that are
   * already written as they are to be read.
   */
  value: number | string;
  /**
   * What the figure is a part of or is measured in, set quietly behind it:
   * the "of 14" of "5 of 14".
   */
  suffix?: string;
  caption?: string;
  // colours the caption, where it is a movement
  captionTone?: Tone;
  // set right after the figure, as an arrow of how it moved
  after?: ReactNode;
  /**
   * Colours the figure itself. Reserved for values that ARE a movement — a
   * deviation, an exposure change — where the direction is the point. A level
   * (a total, an average) stays in ink.
   */
  tone?: Tone;
  sx?: Sx;
}

export const Metric = ({
  label,
  value,
  suffix,
  caption,
  captionTone,
  after,
  tone,
  sx,
}: MetricProps) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const tones = toneColors(theme);

  const display =
    typeof value === "number" ? currency.formatCompact(value) : value;
  // an exact form only exists for a rounded number
  const exact = typeof value === "number" ? currency.format(value) : undefined;

  const figure = (
    <Typography
      variant="h5"
      noWrap
      sx={{
        color: tone && tones[tone],
        fontWeight: "bold",
        ...TABULAR_NUMERALS,
        // block, not inline-block: an inline-block figure sits on a text
        // baseline and carries descender space under it, opening a gap to the
        // caption that the label above doesn't have. fit-content keeps the
        // hover target on the number rather than the full row.
        display: "block",
        width: "fit-content",
        lineHeight: 1.25,
        letterSpacing: "-.015em",
        cursor: exact ? "default" : undefined,
      }}
    >
      {display}
      {suffix && (
        // set off by a space rather than a margin: a space is read out and
        // copied with the two
        <>
          {" "}
          <Box
            component="span"
            sx={{
              typography: "body2",
              fontWeight: 600,
              color: "text.secondary",
            }}
          >
            {suffix}
          </Box>
        </>
      )}
      {after}
    </Typography>
  );

  return (
    <Box sx={sx}>
      <Typography
        variant="caption"
        noWrap
        sx={{
          color: "text.secondary",
          display: "block",
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      {exact ? (
        <Tooltip title={exact} placement="top" arrow>
          {figure}
        </Tooltip>
      ) : (
        figure
      )}
      {caption && (
        <Typography
          variant="caption"
          sx={{
            color: captionTone ? tones[captionTone] : "text.secondary",
            fontWeight: captionTone ? 600 : undefined,
            display: "block",
            ...TABULAR_NUMERALS,
          }}
        >
          {caption}
        </Typography>
      )}
    </Box>
  );
};
